/**
 * Catalogue Zod schemas — shared FE ↔ BE.
 *
 * Per DESIGN.md §5.2 (endpoints 39–40) + ARCHITECTURE.md §3.5 +
 * REQUIREMENTS.md FR-ALUM / FR-UPVC / FR-ALUK.
 *
 * DB source of truth: apps/api/src/infrastructure/db/schema/series.ts
 *
 * The catalogue is read-only in v1.0 — populated exclusively by the
 * seed script (T-M2-010). No write schemas are defined here.
 */
import { z } from 'zod';

/* ─── Material enum ──────────────────────────────────────────────── */

/** Material codes — must match the `material` pgEnum in _enums.ts. */
export const MATERIAL_CODES = ['ALUMINIUM', 'UPVC', 'ALUK'] as const;
export type MaterialCode = (typeof MATERIAL_CODES)[number];

/** Calculator mode — must match the `calc_mode` pgEnum in _enums.ts. */
export const CALC_MODE_CODES = ['SLIDING', 'CASEMENT'] as const;
export type CalcModeCode = (typeof CALC_MODE_CODES)[number];

/* ─── List query ─────────────────────────────────────────────────── */

/**
 * Query-string parameters for `GET /api/v1/catalogue/series`.
 *
 * `material` filters by profile material family. Omit to return all materials.
 */
export const ListSeriesQuerySchema = z.object({
  material: z.enum(MATERIAL_CODES).optional(),
  windowTypeId: z.coerce.number().int().positive().optional(),
});

export type ListSeriesQuery = z.infer<typeof ListSeriesQuerySchema>;

/* ─── Response DTOs ──────────────────────────────────────────────── */

/**
 * A single series row from the per-tenant `series` table.
 * Returned by `GET /api/v1/catalogue/series`.
 */
export const SeriesDtoSchema = z.object({
  id: z.number().int().positive(),
  code: z.string(),
  material: z.enum(MATERIAL_CODES),
  label: z.string(),
  /** Default stock-bar length for this series (mm). Overridable per tenant/project/line-item. */
  defaultStockBarMm: z.number().int().positive(),
});

export type SeriesDto = z.infer<typeof SeriesDtoSchema>;

/**
 * A single type row from the per-tenant `types` table.
 * Returned by `GET /api/v1/catalogue/series/:id/types`.
 */
export const SeriesTypeDtoSchema = z.object({
  id: z.number().int().positive(),
  seriesId: z.number().int().positive(),
  typeCode: z.string(),
  mode: z.enum(CALC_MODE_CODES),
  label: z.string(),
});

export type SeriesTypeDto = z.infer<typeof SeriesTypeDtoSchema>;
